import { agendaFor, orderedStops, splitDay, storeInfo } from '../store'
import { buildEta, pathMiles, roadMiles, windowRange, DEPOT } from '../geo'

/**
 * Turns the day of a salesperson (visits) or a driver (deliveries) into one list of "stop view models" so the same
 * route screen, map and stop cards serve both roles. Order shown: finished stops (as they happened), the one in
 * progress, the ones still to do (route order, with ETA), then missed / skipped ones.
 */

const orderOfVisit = (sales, v) => sales.orders.filter((o) => o.visitId === v.id)[0] || (v.soNo ? sales.orders.filter((o) => o.soNo === v.soNo)[0] : null) || null

export const VISIT_TONE = { order: 'green', no_order: 'amber', closed: 'gray' }
export const VISIT_LABEL = { order: 'Order taken', no_order: 'No order', closed: 'Store closed' }

const goalChip = (info) => {
    if (!info || !info.state) return null
    const s = info.state
    if (s.key === 'nogoal') return null
    if (s.key === 'met') return { tone: 'green', text: 'Goal met' }
    if (s.key === 'behind') return { tone: 'amber', text: 'Behind goal' + (info.gap > 0 ? ' · $' + Math.round(info.gap).toLocaleString('en-US') + ' to go' : '') }
    if (s.key === 'ok' && s.label === 'On track') return { tone: 'blue', text: 'On track' }
    return null
}

/** Salesperson. `from` = last known position (or null), `startMin` = minute of the day the first leg starts. */
export const repStops = (state, actor, date, from, startMin, vIdx, oIdx) => {
    const sales = state.sales
    const agenda = agendaFor(sales, actor.rep, date)
    const sp = splitDay(agenda)
    const etas = buildEta(sp.pending.map((x) => x.store), sp.current ? sp.current.store : from, startMin, 20)
    const make = (x, st, eta) => {
        const v = x.visit
        const s = x.store
        const info = storeInfo(sales, s.id, vIdx, oIdx)
        const order = v.status === 'done' || v.status === 'in_progress' ? orderOfVisit(sales, v) : null
        const chips = []
        if (s.hold) chips.push({ tone: 'red', text: 'Credit hold' })
        if (v.extra) chips.push({ tone: 'purple', text: v.source === 'nearby' ? 'Added · nearby' : 'Added' })
        if (st === 'pending') { const g = goalChip(info); if (g) chips.push(g) }
        if (st === 'pending' && info.missedThisMonth > 0) chips.push({ tone: 'red', text: 'Missed ' + info.missedThisMonth + '× this month' })
        let tone = 'blue'
        let label = 'To visit'
        let detail = ''
        if (st === 'done') {
            tone = VISIT_TONE[v.outcome] || 'green'
            label = VISIT_LABEL[v.outcome] || 'Done'
            detail = v.outcome === 'order' ? (order ? '$' + Math.round(order.total).toLocaleString('en-US') + (order.soNo ? ' · ' + order.soNo : ' · pending sync') : '') : (v.reason || '')
        } else if (st === 'current') { tone = 'cyan'; label = 'In progress' } else if (st === 'missed') { tone = 'red'; label = v.skipped ? 'Skipped' : 'Missed'; detail = v.missReason || '' } else if (v.extra) tone = 'purple'
        return { id: v.id, kind: 'visit', ref: v, store: s, order: order, info: info, name: s.name, address: s.address, city: s.city, phone: s.phone, lat: s.lat, lng: s.lng, state: st, tone: tone, label: label, detail: detail, chips: chips, eta: eta || null, extra: !!v.extra, pending: !!v.pending }
    }
    const list = []
    sp.done.forEach((x) => list.push(make(x, 'done')))
    if (sp.current) list.push(make(sp.current, 'current'))
    sp.pending.forEach((x, i) => list.push(make(x, 'pending', etas[i])))
    sp.missed.forEach((x) => list.push(make(x, 'missed')))
    list.forEach((s, i) => { s.num = i + 1 })
    return list
}

/** Driver. */
export const driverStops = (state, actor, date, from, startMin) => {
    const drv = state.drivers
    const all = orderedStops(drv, date, actor.driverId).filter((d) => d.status !== 'cancelled')
    const done = all.filter((d) => d.status === 'delivered').sort((a, b) => (a.doneAt || 0) - (b.doneAt || 0))
    const current = all.filter((d) => d.status === 'out' && d.arrivedAt)[0] || null
    const pending = all.filter((d) => (d.status === 'planned' || d.status === 'out') && d !== current)
    const etas = buildEta(pending, current || from || DEPOT, startMin, 12)
    const make = (d, st, eta) => {
        const chips = []
        if (d.window) chips.push({ tone: 'blue', text: d.window })
        if (d.terms === 'COD') chips.push({ tone: 'amber', text: 'COD $' + Math.round(d.amount).toLocaleString('en-US') })
        if (d.notes) chips.push({ tone: 'gray', text: d.notes })
        if (d.events.some((e) => e.type === 'failed') && st === 'pending') chips.push({ tone: 'red', text: 'Retry after failed attempt' })
        if (st === 'pending' && eta) {
            const w = windowRange(d.window)
            if (w && eta.arrive > w.end) chips.push({ tone: 'red', text: 'ETA after window' })
        }
        let tone = 'blue'
        let label = d.status === 'out' ? 'Out for delivery' : 'To deliver'
        let detail = ''
        if (st === 'done') { tone = 'green'; label = d.shortLines && d.shortLines.length ? 'Partial delivery' : 'Delivered'; detail = d.receivedBy ? 'Received by ' + d.receivedBy : '' } else if (st === 'current') { tone = 'cyan'; label = 'At the stop' }
        return { id: d.id, kind: 'delivery', ref: d, name: d.customer, address: d.address, city: d.city, phone: d.phone, lat: d.lat, lng: d.lng, state: st, tone: tone, label: label, detail: detail, chips: chips, eta: eta || null, window: d.window, invoiceNo: d.invoiceNo, weightLb: d.weightLb, amount: d.amount, pending: !!d.pending }
    }
    const list = []
    done.forEach((d) => list.push(make(d, 'done')))
    if (current) list.push(make(current, 'current'))
    pending.forEach((d, i) => list.push(make(d, 'pending', etas[i])))
    list.forEach((s, i) => { s.num = i + 1 })
    return list
}

/** Headline numbers of a day for the header card. */
export const summarizeStops = (stops, kind, startFrom) => {
    const done = stops.filter((s) => s.state === 'done').length
    const missed = stops.filter((s) => s.state === 'missed').length
    const current = stops.filter((s) => s.state === 'current').length
    const left = stops.filter((s) => s.state === 'pending').length + current
    const pts = stops.filter((s) => s.state !== 'missed').map((s) => ({ lat: s.lat, lng: s.lng }))
    let mi = pathMiles(pts, kind === 'driver' ? DEPOT : startFrom)
    if (kind === 'driver' && pts.length > 0) mi += roadMiles(pts[pts.length - 1], DEPOT)
    const money = stops.filter((s) => s.state === 'done' && s.order).reduce((a, s) => a + s.order.total, 0)
    const lastEta = stops.filter((s) => s.eta).slice(-1)[0]
    return { total: stops.length, done: done, missed: missed, left: left, miles: Math.round(mi), money: money, endEta: lastEta ? lastEta.eta.leave : null }
}
